///Users/bobbygilbert/Documents/GitHub/TPCTrust-platform/app/[locale]/(routes)/crm/opportunities/table-components/data-table.tsx
"use client";

import * as React from "react"; // Import React
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"; // Import Select components
import { Input } from "@/components/ui/input"; // Import Input

import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "./data-table-toolbar";
import { PanelTopClose, PanelTopOpen } from "lucide-react";

import { Button } from "@/components/ui/button"; // Import Button component
import Papa from "papaparse"; // Import papaparse

// Define a more specific type for the data passed to the table
// Ensure it includes fields used for filtering and display (created_on/createdAt)
interface OpportunityInputData {
    id: string;
    createdAt?: Date | string | null; // Include possible date fields
    created_on?: Date | string | null; // Include possible date fields
    name?: string | null;
    // Add other fields from your Opportunity type definition
    budget?: number | null;
    expected_revenue?: number | null;
    account?: string | null;
    sales_stage?: string | null;
    sales_stage2?: string | null;
    sales_stage3?: string | null;
    assigned_account?: { name?: string | null } | null; // Example nested object
    assigned_leads?: {
      firstName?: string | null;
      lastName?: string | null;
      advisorName?: string | null;
      advisorEmail?: string | null;
      city?: string | null;
      state?: string | null;
    } | null;
    // Include any other fields used by columns or export logic
    [key: string]: any; // Allow other properties
}


interface DataTableProps<TData extends OpportunityInputData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

export function OpportunitiesDataTable<TData extends OpportunityInputData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [search, setSearch] = React.useState("");
  const [hide, setHide] = React.useState(false);
  // Add state for selected year, default to 2025
  const [selectedYear, setSelectedYear] = React.useState<string>("2025");

  // Filter data based on selectedYear
  const filteredDataByYear = React.useMemo(() => {
    return data.filter((opportunity) => {
      const dateField = opportunity.created_on || opportunity.createdAt;
      if (!dateField) {
        // Decide how to handle opportunities without a date - exclude them?
        // Or include them regardless of year? Let's exclude for now.
        // return false;
        // Or if you want undated ones to always show:
         return true; // Modify as needed
      }
      try {
        const createdYear = new Date(dateField).getFullYear().toString();
        return createdYear === selectedYear;
      } catch (e) {
        console.error("Error parsing date for opportunity:", opportunity.id, dateField, e);
        return false; // Exclude if date is invalid
      }
    });
  }, [data, selectedYear]);

  const table = useReactTable({
    // Use the year-filtered data
    data: filteredDataByYear,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(), // Filtering (e.g., search) happens AFTER year filter
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  // Search handler remains the same
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    // Apply filter to name field only
    table.getColumn("name")?.setFilterValue(value); // Use table's filter method
  };

  // --- Mappings and Formatters (Keep as is) ---
  const accountMapping: { [key: string]: string } = {
    clu7nn3in00067p77cwuq0qbd: "Bevel", clu7nocab00087p77ul59mab4: "Craft Capital",
    cluqzlo7f0001qgkks8v16kuc: "Other Financing", cluqzn7yz0003qgkkvtb1fsbj: "Cash",
  };
  const salesStageMapping: { [key: string]: string } = {
    "clu7mz9fh000fijpzzcr2o7wo": "Documents Received", "clu7mz9fh000xijpzbackburn": "Backburner",
    "clu7mz9fh000gijpz7n2n93rq": "In Financing", "clu7mz9fh000hijpz7qkygf03": "Pending Equipment",
    "clu7mz9fh000xijpzprior123": "Priority", "clu7mz9fh000iijpzh5hz7oh5": "SO Created",
    "clu7mz9fh000jijpzlp2le32w": "Payment pending", "clu7mz9fh000kijpztf43pmeb": "Promissory note completed",
    "clu7mz9fh000lijpzkxsroj0d": "Deposit and platform fee paid", "clu7mz9fh000mijpz0l2pwqzt": "Sale confirmed",
    "clu7mz9fh000nijpz0lv0eou5": "Sign re-purchase agreement"
  };
  const salesStage2Mapping: { [key: string]: string } = {
    clu7mz9rh000oijpz2pebrfch: "Deal Reviewed by TPC", clu7mz9rh000pijpzkg5zi7zg: "Finance Partners Initial Review",
    cltwi0l9w000qo4xzi3rj6brp: "Refer to Lender", cltwi0l9w000ro4xz9n1m2kgp: "Lenders Initial Review",
    cltwi0l9w000so4xzhckmvm8t: "Accepted", cltwi0l9w000to4xzflrwkuqn: "Declined",
    cltwi0l9w000uo4xzoaz8yrft: "Additional Information Needed", cltwi0l9w000vo4xzincbm7va: "Term Sheet Offered",
    cltwi0l9w000wo4xzj8vw4h1k: "Term Sheet Accepted",
  };
  const salesStage3Mapping: { [key: string]: string } = {
    clu7mza3d0014jjpzne89xep1: "Yet to Start", clu7mza3d0015ijpzk3cv1adj: "Term Sheet Accepted",
    clu7mza3d0010ijpz0eybj2rr: "Lending Partner Need from Client?", clu7mza3d00zjjpzgpbwjiy8z: "What Bank is Loan At?",
    clu7mza3d000xijpzxe58l3ni: "Bridge Docs – Signed", clu7mza3d000yjjpzu72gr34y: "EZ Side Complete",
  };
  const formatDate = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return "N/A";
    try {
        const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "2-digit", day: "2-digit" };
        return new Date(dateString).toLocaleDateString(undefined, options);
    } catch {
        return "Invalid Date";
    }
  };
  const formatCurrency = (amount: number | string | null | undefined): string => {
    if (amount === null || amount === undefined) return "N/A";
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return "Invalid Amount";
    return new Intl.NumberFormat('en-US', { style: "currency", currency: "USD" }).format(numAmount);
  };
  // --- End Mappings and Formatters ---

  // Mapping function for export - uses already filtered data
  const mapDataForExport = (exportTableData: TData[]) => {
    return exportTableData.map((row) => ({
      Created: formatDate(row.created_on || row.createdAt), // Use helper
      Name: row.name || "N/A", // Handle potential null
      Account: accountMapping[row.account || ""] || row.assigned_account?.name || row.account || "N/A", // More robust fallback
      Amount: formatCurrency(row.budget), // Use budget, format via helper
      "Equipment Financing and Sale Process Pipeline": salesStageMapping[row.sales_stage || ""] || "N/A",
      "Deal Review and Lending Process Pipeline": salesStage2Mapping[row.sales_stage2 || ""] || "N/A",
      "Bridge Note Pipeline": salesStage3Mapping[row.sales_stage3 || ""] || "N/A",
      City: row.assigned_leads?.city || "N/A",
      State: row.assigned_leads?.state || "N/A",
      Advisor: row.assigned_leads?.advisorName || "N/A",
      "Investor Name": row.assigned_leads
        ? `${row.assigned_leads.firstName || ""} ${row.assigned_leads.lastName || ""}`.trim() || "N/A"
        : "N/A",
    }));
  };

  // CSV Export Function - Now uses the filtered data from the table instance
  const handleExport = () => {
    // Get the data currently being displayed by the table (respects all filters)
    const currentTableData = table.getFilteredRowModel().rows.map(row => row.original);
    const exportData = mapDataForExport(currentTableData); // Map this filtered data

    const csvHeaders = [
      "Created", "Name", "Account", "Amount",
      "Equipment Financing and Sale Process Pipeline",
      "Deal Review and Lending Process Pipeline",
      "Bridge Note Pipeline",
      "City", "State", "Advisor", "Investor Name",
    ];

    const csv = Papa.unparse({
      fields: csvHeaders,
      data: exportData.map((row) => [ // Map the formatted exportData
        row.Created, row.Name, row.Account, row.Amount,
        row["Equipment Financing and Sale Process Pipeline"],
        row["Deal Review and Lending Process Pipeline"],
        row["Bridge Note Pipeline"],
        row.City, row.State, row.Advisor, row["Investor Name"],
      ]),
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    // Include year in filename
    link.setAttribute("download", `opportunities_${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-4">
     <div className="flex items-center justify-between gap-3"> {/* Use items-center */}
        <div className="flex items-center gap-3"> {/* Group search and year filter */}
            {/* Search Input */}
            <Input // Use Shadcn Input
                type="text"
                value={search}
                onChange={handleSearch}
                placeholder="Search by name..."
                className="max-w-sm h-9" // Control size
            />
            {/* Year Filter Select */}
            <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[120px] h-9"> {/* Control size */}
                    <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2024">2024</SelectItem>
                    {/* Add more years if needed */}
                </SelectContent>
            </Select>
        </div>

        <div className="flex items-center space-x-2"> {/* Group export and hide */}
            {/* Export Button */}
            <Button onClick={handleExport} variant="secondary" size="sm"> {/* Control size */}
            Export CSV
            </Button>

            {/* Hide/Show Toggle */}
            {hide ? (
                <Button variant="ghost" size="icon" onClick={() => setHide(false)} title="Show Table">
                    <PanelTopOpen className="w-5 h-5 text-muted-foreground" />
                </Button>
            ) : (
                <Button variant="ghost" size="icon" onClick={() => setHide(true)} title="Hide Table">
                    <PanelTopClose className="w-5 h-5 text-muted-foreground" />
                </Button>
            )}
        </div>
      </div>

      {hide ? (
        <div className="flex items-center gap-2 p-4 border rounded-md text-muted-foreground">
          Table content is hidden. Click the <PanelTopOpen className="inline w-5 h-5 mx-1" /> icon to show.
        </div>
      ) : (
        <>
          {/* Pass search value down if toolbar uses it, or handle filtering via table instance */}
          <DataTableToolbar table={table} />
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} colSpan={header.colSpan}> {/* Use colSpan */}
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results for {selectedYear}.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <DataTablePagination table={table} />
        </>
      )}
    </div>
  );
}
